import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/LanguageSelector";
import { MenuSelect } from "../../components/Form";
import { AiOutlinePoweroff } from "react-icons/ai";
import { logout } from "../../api/auth-scripts";
import UserContext from "../../context/UserContext";
import { useContext } from "react";

function PatientHealthForm() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setUser } = useContext(UserContext);

  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await api.get("/healthform/clinic-health-form");
        const q = res.data?.questions || [];

        if (q.length === 0) {
          navigate("/patient");
          return;
        }

        setQuestions(q);
      } catch (err) {
        toast.error(t("healthform.failedtoload"));
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  const handleChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = async () => {
    const payload = {
      clientId,
      answers: questions.map((q) => ({
        questionId: q.id,
        answer: responses[q.id] || "",
      })),
    };

    try {
      await api.post("/healthinfo", payload);
      toast.success(t("healthform.formsaved"));
      navigate("/patient");
    } catch (err) {
      toast.error(t("healthform.errorsaving"));
    }
  };

  const DropDown = [
    {
      title: t("dropdown.logout"),
      icon: AiOutlinePoweroff,
      onClick: () => logout(navigate, toast, setUser),
    },
  ];

  if (loading)
    return <p className="p-6 text-center">{t("healthform.errorform")}</p>;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-100">
      {/* Language Selector */}
      <div className="absolute top-4 left-1 md:left-5">
        <LanguageSelector />
      </div>

      {/* User Menu */}
      <div className="absolute top-0 right-1 md:right-5 flex items-center md:flex">
        <MenuSelect datas={DropDown}>
          <div className="flex gap-4 items-center p-4 rounded-lg">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-subMain text-white rounded-full flex items-center justify-center font-medium text-base">
              P
            </div>
            <p className="text-sm text-textBlack font-medium hidden md:flex">
              {t("patient_form.title")}
            </p>
          </div>
        </MenuSelect>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="bg-white mt-10 p-6 md:p-8 rounded-lg shadow-lg w-full max-w-2xl"
      >
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center text-gray-800">
          {t("healthinfo.title")}
        </h2>

        {questions.map((question) => (
          <div key={question.id} className="mb-6">
            <label className="block font-medium mb-2 text-gray-700">
              {question.questionText}
            </label>

            {question.questionType === "multiple_choice" ? (
              <select
                className="w-full border p-3 rounded bg-white"
                value={responses[question.id] || ""}
                onChange={(e) => handleChange(question.id, e.target.value)}
              >
                <option value="" disabled>
                  {t("healthform.options")}
                </option>
                {question.options.map((opt, idx) => (
                  <option key={idx} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <textarea
                rows={3}
                className="w-full border p-3 rounded"
                placeholder={t("healthform.enterquestion")}
                value={responses[question.id] || ""}
                onChange={(e) => handleChange(question.id, e.target.value)}
              />
            )}
          </div>
        ))}

        <button
          type="submit"
          className="bg-subMain text-white px-6 py-3 rounded font-semibold w-full"
        >
          {t("healthform.saveform")}
        </button>
      </form>
    </div>
  );
}

export default PatientHealthForm;
